import { Alert, InputAdornment, List, Stack, TextField, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { DialogBox } from "lib/@components/dialogBox"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { PagedRepeat } from "lib/@components/paged-repeat"
import { useEffect, useMemo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { ClientDataMigrationItem } from "./client-data-migration-item"
import { SelectAllCheckbox } from "lib/@components/select-all-checkbox"
import { setFromEvent } from "lib/setFromEvent"
import { If, Else } from "lib/@components/switch"
import { useDebouncedValue } from "lib/@hooks/useDebouncedValue"
import sortBy from "lodash/sortBy"
import keyBy from "lodash/keyBy"

const compactCheckboxSx = { px: 1, py: 0, ".MuiListItemIcon-root": { minWidth: 32 } }
// Fill the fixed-height dialog and let the content scroll; without minHeight 0 the content grows
// to fit the list and the dialog box clips it instead of scrolling
const fillDialogSx = { flex: 1, minHeight: 0, "& .MuiDialogContent-root": { minHeight: 0 } }
// The search and bulk selects stay pinned while the list scrolls under them
const stickyHeaderSx = { position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", pb: 1 }

// Module-level defaults keep a stable identity across renders — inline defaults would
// recompute the transformed memo and re-fire the reconcile effect on every render
const defaultTransform = (v) => v
const defaultGetKey = (item) => item

export function ClientDataMigrationDialog({
    type,
    subscriptionId,
    transform = defaultTransform,
    query,
    warning,
    getKey = defaultGetKey,
}) {
    const { data: preTransformedData = [], loading } = query.useResults.status(subscriptionId)
    const [selectedItems, setSelectedItems] = useState([])
    // debouncedSearch drives the filter so a full re-scan doesn't run on every keystroke at scale
    const [search, setSearch, debouncedSearch] = useDebouncedValue("", 250)
    const [pageItems, setPageItems] = useState([])

    // Transform once per fetch (not per search) so item identity is stable while searching/paging
    const transformed = useMemo(transformData, [preTransformedData, transform])
    const data = useMemo(filterAndSortData, [transformed, debouncedSearch])
    const selectedKeys = useMemo(getSelectedKeys, [selectedItems, getKey])
    const selectable = useMemo(getSelectable, [data])
    const selectablePage = useMemo(getSelectablePage, [pageItems])
    const numSelectedInAll = useMemo(countSelectedInAll, [selectable, selectedKeys])
    const numSelectedInPage = useMemo(countSelectedInPage, [selectablePage, selectedKeys])
    // selected items are never migrated, so anything selected outside `selectable` is hidden by the search
    const numSelectedHidden = selectedItems.length - numSelectedInAll

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(reconcileSelectionWithFreshData, [transformed])

    if (loading) return <LoadingScreen />

    return (
        <Bound selectedItems={selectedItems} selectedKeys={selectedKeys} getKey={getKey}>
            <DialogBox
                title={`Migrate ${type.titleize()}`}
                value={selectedItems}
                okDisabled={selectedItems.length < 1}
                okCaption={confirmCaption()}
                sx={fillDialogSx}
            >
                <Stack spacing={1} mt={1}>
                    <If value={!!warning}>
                        <Alert severity="warning" data-cy="migration-warning">
                            <Typography variant="body2">{warning}</Typography>
                        </Alert>
                    </If>
                    <Stack spacing={1} sx={stickyHeaderSx}>
                        <TextField
                            value={search}
                            onChange={setFromEvent(setSearch)}
                            onKeyDown={handleSearchKeyDown}
                            fullWidth
                            autoFocus
                            type="search"
                            variant="standard"
                            placeholder={`Search ${type}...`}
                            inputProps={{ "data-cy": "migration-search" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {/* The selection summary and warnings stay outside the filtered branch — a
                        zero-match search must not hide what an enabled Confirm will act on */}
                        <If value={data.length > 1 || selectedItems.length > 0}>
                            <Stack direction="row" spacing={1} alignItems="center" data-cy="migration-bulk-select">
                                <If value={data.length > 1}>
                                    <SelectAllCheckbox
                                        data={selectable}
                                        numSelected={numSelectedInAll}
                                        label={selectAllLabel()}
                                        onSelect={handleSelectAll}
                                        sx={compactCheckboxSx}
                                    />
                                    {/* Select page duplicates select all when everything fits on one page */}
                                    <If value={pageItems.length < data.length}>
                                        <SelectAllCheckbox
                                            data={selectablePage}
                                            numSelected={numSelectedInPage}
                                            label={`Select page (${selectablePage.length})`}
                                            dataCy="select-page-checkbox"
                                            onSelect={handleSelectPage}
                                            sx={compactCheckboxSx}
                                        />
                                    </If>
                                </If>
                                <If value={selectedItems.length > 0}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        data-cy="selected-count"
                                        sx={{ ml: "auto", whiteSpace: "nowrap", flexShrink: 0, pr: 1 }}
                                    >
                                        {selectedCountLabel()}
                                    </Typography>
                                </If>
                            </Stack>
                        </If>
                    </Stack>
                    <If value={data && data.length > 0}>
                        <List dense sx={{ py: 0 }}>
                            <PagedRepeat
                                list={data}
                                item={<ClientDataMigrationItem onSelect={handleToggle} />}
                                onPageItemsChange={setPageItems}
                            />
                        </List>
                        <Else>
                            <Typography mt={2} data-cy="migration-empty">
                                {emptyLabel()}
                            </Typography>
                        </Else>
                    </If>
                </Stack>
            </DialogBox>
        </Bound>
    )

    function transformData() {
        return (preTransformedData ?? []).map(transform)
    }

    function filterAndSortData() {
        const term = debouncedSearch.toLowerCase()
        const filtered = transformed.filter((d) => d.label.toLowerCase().includes(term))
        return sortBy(filtered, (d) => (d.migrated ? 1 : 0))
    }

    function getSelectedKeys() {
        return new Set(selectedItems.map(getKey))
    }

    function getSelectable() {
        return data.filter((d) => !d.migrated)
    }

    function getSelectablePage() {
        return pageItems.filter((d) => !d.migrated)
    }

    function countSelected(items) {
        return items.filter((d) => selectedKeys.has(getKey(d))).length
    }

    function countSelectedInAll() {
        return countSelected(selectable)
    }

    function countSelectedInPage() {
        return countSelected(selectablePage)
    }

    function isSearching() {
        return debouncedSearch.trim().length > 0
    }

    function selectAllLabel() {
        return isSearching() ? `Select all matching (${selectable.length})` : `Select all (${selectable.length})`
    }

    function selectedCountLabel() {
        return numSelectedHidden > 0
            ? `${selectedItems.length} selected (${numSelectedHidden} not shown)`
            : `${selectedItems.length} selected`
    }

    function emptyLabel() {
        return isSearching() ? `No ${type} match "${debouncedSearch.trim()}"` : `No ${type} available`
    }

    function confirmCaption() {
        return selectedItems.length > 0 ? `Migrate ${selectedItems.length}` : "Migrate"
    }

    function reconcileSelectionWithFreshData() {
        // wait for the delivery — reconciling against in-flight data would drop everything
        if (loading) return
        // when the query re-delivers, swap selected items for the latest row data by key and
        // drop ones the refreshed data no longer contains, so Confirm can't submit ids that
        // are no longer available; return the same array when nothing changed so the state
        // update bails out
        const byKey = keyBy(transformed, getKey)
        setSelectedItems((current) => {
            const next = current.map((item) => byKey[getKey(item)]).filter((item) => item && !item.migrated)
            const unchanged = next.length === current.length && next.every((item, i) => item === current[i])
            return unchanged ? current : next
        })
    }

    function handleToggle(item) {
        if (item.migrated) return
        const key = getKey(item)
        if (selectedKeys.has(key)) {
            setSelectedItems((c) => c.filter((i) => getKey(i) !== key))
        } else {
            setSelectedItems((c) => [...c, item])
        }
    }

    function handleSelectAll(allChecked) {
        applyBulkSelection(selectable, allChecked)
    }

    function handleSelectPage(allChecked) {
        applyBulkSelection(selectablePage, allChecked)
    }

    function handleSearchKeyDown(e) {
        if (e.key === "Enter") {
            e.stopPropagation()
        }
    }

    function applyBulkSelection(items, remove) {
        const keys = new Set(items.map(getKey))
        if (remove) {
            setSelectedItems((c) => c.filter((i) => !keys.has(getKey(i))))
        } else {
            setSelectedItems((c) => [...c.filter((i) => !keys.has(getKey(i))), ...items])
        }
    }
}
